<script lang="ts" setup>
import type {
  PaymentIntent,
  PaymentMethodType,
} from '@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types'

const props = withDefaults(
  defineProps<{
    currency: Currency
    intent: SubscriptionPaymentIntent
    paymentMethodTypes?: PaymentMethodType[]
  }>(),
  {
    paymentMethodTypes: () => ['card'],
  }
)

const emit = defineEmits<{
  success: [event: PaymentComponentSuccessEvent]
  error: [event: PaymentComponentErrorEvent]
  valid: []
  update: [event: PaymentComponentUpdateEvent]
}>()

const { loadChargebee, cbInstance } = useChargebee()
const { locale } = useI18n()
const businessStore = useBusinessStore()
const userStore = useUserStore()

const paymentComponentInstance = shallowRef<ChargebeePaymentComponent | null>(null)
const containerRef = ref<HTMLDivElement>()
const error = ref<ChargebeeError | null>(null)
const isLoading = ref(true)
const isUpdatingIntent = ref(true)
const isSubmitting = ref(false)
const selectedPaymentMethod = ref<PaymentMethodType | null>(null)

const businessName = computed(() => {
  if (businessStore.name) return businessStore.name
  else return `${userStore.firstName} ${userStore.lastName}`
})

const businessEmail = computed(() => {
  if (businessStore.email) return businessStore.email
  else return userStore.email
})

const onPaymentMethodChange = (paymentMethod: PaymentMethodType) => {
  if (isUpdatingIntent.value) {
    isUpdatingIntent.value = false
    return
  }

  if (selectedPaymentMethod.value === paymentMethod) {
    return
  }

  selectedPaymentMethod.value = paymentMethod
  emit('update', { paymentMethodType: paymentMethod })
}

const onSuccess = (paymentIntent: PaymentIntent) => {
  emit('success', { paymentIntentId: paymentIntent.id })
}

const onError = (e: PaymentComponentErrorEvent) => {
  error.value = e.error
  emit('error', { error: e.error })
}

const createPaymentComponent = async () => {
  if (!cbInstance.value || !props.intent) {
    return
  }

  const components = cbInstance.value.components({
    locale: locale.value,
    style: {
      variables: {
        colorBackground: '$c-grey-fine',
      },
    },
  })

  if (paymentComponentInstance.value) {
    paymentComponentInstance.value.close()
    paymentComponentInstance.value = null
  }

  paymentComponentInstance.value = components.create(
    'payment',
    {
      paymentIntent: props.intent.paymentIntent,
      layout: { type: 'tab', showRadioButtons: true },
      paymentMethods: {
        sortOrder: props.paymentMethodTypes,
        allowed: props.paymentMethodTypes,
      },
      context: {
        customer: {
          companyName: businessName.value,
          email: businessEmail.value,
          firstName: userStore.firstName,
          lastName: userStore.lastName,
        },
      },
    },
    {
      onSuccess: onSuccess,
      onPaymentMethodChange: onPaymentMethodChange,
      onError: onError,
    }
  )

  if (paymentComponentInstance.value && containerRef.value) {
    await paymentComponentInstance.value.mount(containerRef.value)
  }
}

const initPaymentComponent = async () => {
  const instance = await loadChargebee()
  if (!instance) {
    isLoading.value = false
    return
  }
  try {
    await createPaymentComponent()
  } catch (err) {
    error.value = err as ChargebeeError
    emit('error', { error: error.value })
  } finally {
    isLoading.value = false
  }
}
onMounted(async () => {
  initPaymentComponent()
})

watch(
  () => props.paymentMethodTypes,
  async (newVal, oldVal) => {
    const unchanged = newVal.length === oldVal.length && newVal.every((v, i) => v === oldVal[i])
    if (unchanged) return

    try {
      await createPaymentComponent()
    } catch (err) {
      error.value = err as ChargebeeError
      emit('error', { error: error.value })
    }
  },
  { deep: true }
)

watch(
  () => props.intent,
  (newIntent, oldIntent) => {
    if (!newIntent || newIntent.paymentIntent.id === oldIntent?.paymentIntent.id) return

    isUpdatingIntent.value = true
    paymentComponentInstance.value?.update({
      paymentIntent: newIntent.paymentIntent,
    })
  }
)

onBeforeUnmount(() => {
  if (paymentComponentInstance.value) {
    paymentComponentInstance.value.close()
    paymentComponentInstance.value = null
  }
})

const submit = () => {
  if (isSubmitting.value || !paymentComponentInstance.value) {
    return
  }

  isSubmitting.value = true

  try {
    paymentComponentInstance.value.confirm()
  } catch (err) {
    error.value = err as ChargebeeError
    emit('error', { error: error.value })
  } finally {
    isSubmitting.value = false
  }
}

const update = (options: Partial<PaymentComponentOptions>) => {
  paymentComponentInstance.value?.update(options)
}

const validate = async () => {
  return await paymentComponentInstance.value?.validate()
}

defineExpose({
  submit,
  cbInstance,
  isLoading,
  update,
  validate,
})
</script>

<template>
  <div class="payment-element">
    <skr-spinner v-if="isLoading" size="small" />
    <div v-show="!isLoading" ref="containerRef" class="payment-element-container" />
  </div>
</template>

<style lang="scss">
.payment-element {
  padding: 20px;
  border: 0;
  border-radius: 12px;
  background: $c-grey-fine;
  min-height: 410px;
}
</style>
